<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useSitesStore } from '@/stores/sites';
    const sitesStore = useSitesStore();

    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    // Composables
    const { $listen } = useNuxtApp();

    // Emits
    const emit = defineEmits(['closeItineraryDetail']);
    defineExpose({
        closeItineraryDetail,
    });

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        shared: {
            type: Boolean,
            required: false,
        },
    });

    // Variables
    const sites = sitesStore.sitesData;

    const router = useRouter();
    const el = ref(null);
    const mapView = ref(false);
    const listView = ref(true);
    const directionsView = ref(false);
    const openEdit = ref(false);
    const cards = ref([]);

    const { ghostItinerary } = storeToRefs(userStore);

    // Methods
    function viewMap() {
        mapView.value = true;
        listView.value = false;
        directionsView.value = false;
    }

    function viewList() {
        listView.value = true;
        mapView.value = false;
        directionsView.value = false;
    }

    function openDirections() {
        directionsView.value = true;
        mapView.value = false;
        listView.value = false;
    }

    function hideDirections() {
        if (import.meta.client) {
            directionsView.value = false;
            mapView.value = true;
        }
    }

    // The bottom nav no longer exists on most layouts (replaced by the global
    // footer, Asana 1217333501985924). These were unguarded property writes on
    // getElementById, which throw a TypeError on null — that would have broken
    // opening and closing the itinerary editor outright, not just its z-order.
    function setBottomNavZIndex(value) {
        const nav = document.getElementById('bottom-navigation');
        if (nav) nav.style.zIndex = value;
    }

    // `top` is deliberately NOT set here any more. It used to be written inline
    // as 0 when editing and '59px' when not — 59px being the MOBILE header —
    // so on desktop, where the chrome is an 88px header plus a 62px nav row,
    // the panel sat 91px underneath the nav with its own top strip hidden.
    //
    // An inline pixel value could not have been right in both places anyway:
    // it wins over any media query, and it never updates when the window
    // crosses the breakpoint. The `edit-open` class already tracks this exact
    // state, so the offset lives in CSS against the same --header-height and
    // --nav-row-height that layouts/default.vue uses to clear #main.
    function edit() {
        openEdit.value = true;
        el.value.style.backgroundColor = 'transparent';
        setBottomNavZIndex(2);
    }

    function closeEdit() {
        openEdit.value = false;
        el.value.style.backgroundColor = 'white';
        setBottomNavZIndex(999);
    }

    function getItinerarySites() {
        let list = [];
        props.itinerary.sites.forEach((item) => {
            sites.forEach((singleSite) => {
                if (item === singleSite.id) {
                    list.push(singleSite);
                }
            });
        });
        return list;
    }

    function closeItineraryDetail() {
        router.replace({ query: null });
        emit('closeItineraryDetail');
    }

    function updateGhostItinerary(itinerary) {
        userStore.setGhostItinerary(itinerary);
    }

    // Lifecycle
    onMounted(() => {
        cards.value = getItinerarySites();
        if (import.meta.client) {
            $listen('itineraryDeleted', () => {
                closeItineraryDetail();
            });
        }
        userStore.setGhostItinerary(props.itinerary);
        userStore.setGhostBaseItinerary(props.itinerary);
    });

    watch(
        () => props.itinerary,
        () => {
            cards.value = getItinerarySites();
        },
        { deep: true }
    );
</script>

<template>
    <section class="itinerary-detail container" ref="el" :class="openEdit ? 'edit-open' : ''">
        <div class="itinerary-detail-inner">
            <div v-show="!openEdit">
                <nav class="hd-container">
                    <button @click.prevent="closeItineraryDetail" aria-label="Back">
                        <SvgsBackButton />
                    </button>
                    <div class="lower">
                        <p class="gray-tag">My Itineraries</p>
                        <ul class="map-list-buttons">
                            <li>
                                <button class="map view-button" @click.prevent="viewMap" aria-label="Map View">
                                    <SvgsMapFolded class="map-folded" :class="{ active: mapView }" />
                                </button>
                            </li>
                            <li>
                                <button class="list view-button" @click.prevent="viewList" aria-label="List View">
                                    <SvgsList class="list" :class="{ active: !mapView }" />
                                </button>
                            </li>
                        </ul>
                    </div>
                </nav>
                <header id="itinerary-header" class="hd-container">
                    <h2 class="headline-1">{{ itinerary.name }}</h2>
                    <button v-if="shared" @click.prevent="$emit('save')" class="small-button turquoise">Save Itinerary</button>
                    <button v-else @click.prevent="edit" class="small-button turquoise">Edit Itinerary</button>
                    <GlobalActions
                        :item="itinerary"
                        :useContainer="false"
                        :useFavorite="false"
                        :useItinerary="false"
                        :useDirections="false"
                        :useDirectionsSingle="false"
                        @openDirections="openDirections" />
                </header>
                <div v-show="mapView" class="map-section">
                    <BlocksMap :locations="cards" :itinerary="itinerary" navId="itinerary-header" :offset="46"  />
                </div>
                <div v-show="listView" class="list-section">
                    <BlocksSitesListStack :cards="cards" componentLocation="itinerary" :itinerary="itinerary" />
                </div>
                <div v-if="directionsView" class="directions-section">
                    <Directions v-if="directionsView" :cards="cards" canToggleDirections @hideDirections="hideDirections" />
                </div>
            </div>
            <ItinerariesEdit v-if="openEdit" @closeEdit="closeEdit" @updateGhostItinerary="updateGhostItinerary" :itinerary="ghostItinerary" :cards="cards" />
        </div>
    </section>
</template>

<style lang="scss" scoped>
    .itinerary-detail {
        overflow: auto;
        z-index: 3;
        position: fixed;
        // Mobile: the header is the only fixed chrome, and --nav-row-height is
        // 0 below $desktop, so the desktop rule below collapses to the same
        // value. Kept as the static fallback for a first paint before the
        // custom properties resolve.
        top: $header-height;
        // The space below the header, not 100svh: a full viewport starting
        // 59px down ran 59px past the bottom of the screen (Asana O2-71).
        height: calc(100svh - #{$header-height});

        @media (min-width: $desktop) {
            top: calc(var(--header-height) + var(--nav-row-height));
            // Sized to the space left over, not 100svh. With a 150px offset a
            // full-viewport height would push the last 150px of the itinerary
            // below the fold, and a position:fixed element cannot be scrolled
            // back into view by the page.
            height: calc(100svh - var(--header-height) - var(--nav-row-height));
        }
        right: 0;
        bottom: 0;
        left: 0;
        padding-top: 15px;
        padding-bottom: calc(70px + env(safe-area-inset-bottom));
        background: white;
        transform: translateY(0);
        transition: transform 0.3s ease-in-out, background-color 0.3s ease-in-out;

        @starting-style {
            transform: translateY(100%);
        }

        // Editing is a full-screen takeover — edit() sets top: 0 inline and
        // demotes the bottom nav to z-index 2 — but this element kept z-index 3
        // while the desktop header and teal nav row sit at 99 and 98. So the nav
        // painted over the top 150px of the editor.
        //
        // That is the unfinished half of Asana O2-54, "you can't click apply,
        // reset, or X". Apply was fixed; Reset and the X were not, because they
        // are the only two controls that land inside the nav's band. Measured on
        // production at 1200x761: Reset at y=142 and X at y=137 against a nav
        // ending at y=150, both reporting nav-desktop as the topmost element,
        // while Apply at y=606 was fine. The same panel on /sites has always
        // worked, which is what ruled out the filter component itself.
        //
        // It has to go here rather than on .itinerary-edit: this element carries
        // a transform, so it is the outermost stacking context of the whole
        // subtree and clamps everything inside it. .itinerary-edit already asks
        // for 9999 and still loses.
        &.edit-open {
            top: 0;
            height: 100svh;
            z-index: 100;
        }

        .tours-overview & {
            background: white;
        }

        &::after {
            content: '';
            display: inline-block;
            position: absolute;
            top: 59px;
            left: 0;
            right: 0;
            bottom: 0;
            background: white;
            z-index: -1;
        }

        &::before {
            content: '';
            display: inline-block;
            position: absolute;
            top: -59px;
            left: 0;
            right: 0;
            height: 59px;
            background: $gray-darkest;
            background-blend-mode: multiply;
            opacity: 0.7;
        }

        @media (min-width: $tablet) {
            &.edit-open::before {
                width: 100%;
                height: 100%;
                top: 0;
            }
        }

        .itinerary-detail-inner {
            background: white;

            @media (min-width: $tablet) {
                width: 100%;
                max-width: $hd;
                margin: 0 auto;
            }
        }

        nav {
            margin-bottom: 10px;

            .lower {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .map-list-buttons {
                display: flex;
                align-items: center;
                margin-left: auto;

                > li:first-of-type {
                    margin-right: 15px;
                }

                @media (min-width: $tablet) {
                    .view-button {
                        &:not(:last-child) {
                            margin-right: 20px;
                        }                        
                    }

                    svg {
                        width: 33px;
                        height: 30px;
                    }
                }
            }
        }

        header {
            h2 {
                margin-bottom: 15px;
            }

            .small-button {
                margin-bottom: 20px;
            }
        }

        .map-section,
        .directions-section {
            margin-top: 30px;
        }

        @media (min-width: $tablet) {
            .sites-list-stack .container {
                padding: 0;
            }
        }
    }
</style>
